/**
 * NOC-OTA-COMPAT-GUARD — classificação de compatibilidade de release OTA app (UI).
 *
 * Espelha backend/app/services/ota_compat.py para que a UI mostre a MESMA decisão
 * que o backend vai aplicar (evita surpresa: o backend é a autoridade e recusa
 * 409/412, mas a UI gateia o botão e exige a frase antes de disparar).
 *
 * Puro / sem React / sem I/O → testável em node (frontend/test/ota_compat.test.mjs).
 * Motivação: incidente IPWD-D4EB9A88 (2.8.9→2.8.12 resetou NVS/config).
 */

// Devices em fw < 2.8.10 antecedem a estabilização do schema de config.
export const SCHEMA_STABLE_MIN = [2, 8, 10];
export const RESET_CONFIRM_PHRASE = "ENTENDO QUE PODE RESETAR CONFIG";

const VER_RE = /(\d+)\.(\d+)\.(\d+)/;

export function parseVersion(v) {
  if (!v) return null;
  const m = VER_RE.exec(String(v));
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function cmp(a, b) {
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

function has(text, ...needles) {
  const t = (text || "").toLowerCase();
  return needles.some((n) => t.includes(n));
}

export function classifyRelease(version, notes = "", kind = "app") {
  const v = (version || "").toLowerCase();
  const n = (notes || "").toLowerCase();
  const factory =
    has(v, "-fe-lab", "-fe-dev", "fe-dev") ||
    has(n, "factory", "tools only", "tools-only", "full-flash", "full flash",
      "5 bins", "efuse", "flash encryption", "fe-dev");
  const commercial = has(v, "shop", "commercial", "infortech") || has(n, "infortechshop", "comercial");
  const provider = !commercial && !factory && has(v, "client", "tower", "provider");
  const plaintext_migration =
    has(v, "plaintext-migration") || (has(n, "migration") && has(n, "plaintext"));
  return {
    kind,
    factory_tools_only: factory,
    requires_fe: factory,
    commercial,
    provider,
    plaintext_migration,
    may_reset_config: plaintext_migration,
  };
}

export function deviceIsCommercial(deviceFw) {
  return classifyRelease(deviceFw).commercial;
}

export function schemaResetRisk(deviceFw, releaseVersion, cls) {
  if (cls && cls.plaintext_migration) return true;
  const dv = parseVersion(deviceFw);
  const tv = parseVersion(releaseVersion);
  if (dv === null) return false;
  if (cmp(dv, SCHEMA_STABLE_MIN) < 0 && (tv === null || cmp(tv, SCHEMA_STABLE_MIN) >= 0)) {
    return true;
  }
  return false;
}

/**
 * @returns {{decision:'allow'|'block'|'needs_reset_confirm', code, message, classification, deviceFw, target}}
 */
export function evaluateOtaCompat(deviceFw, release, { confirmConfigReset = false } = {}) {
  const version = release?.version;
  const notes = release?.notes;
  const kind = release?.kind || "app";
  const cls = classifyRelease(version, notes, kind);
  const base = { classification: cls, deviceFw: deviceFw || null, target: version || null };

  if (cls.factory_tools_only) {
    return {
      ...base, decision: "block", code: "factory_tools_only",
      message:
        "Release factory/Tools-only (FE-dev): precisa de full-flash dos 5 bins pelo Tools " +
        "(com Flash Encryption no 1º boot). NÃO é OTA app — brica um device plaintext.",
    };
  }
  if (cls.commercial && !deviceIsCommercial(deviceFw)) {
    if (!confirmConfigReset) {
      return {
        ...base, decision: "block", code: "commercial_on_noncommercial",
        message:
          "Release COMERCIAL (loja/InfortechShop: AP aberto + admin/admin) num device que " +
          "NÃO é comercial. Muda o comportamento do device.",
      };
    }
  }
  if (schemaResetRisk(deviceFw, version, cls) && !confirmConfigReset) {
    return {
      ...base, decision: "needs_reset_confirm", code: "config_reset_risk",
      message:
        `O salto ${deviceFw || "?"} → ${version || "?"} pode RESETAR a configuração ` +
        "(Wi-Fi/watchdog/agenda) por evolução de schema NVS — o device pode voltar em modo AP " +
        "e ficar offline (incidente IPWD-D4EB9A88). Planeje reconfiguração pós-OTA.",
    };
  }
  return { ...base, decision: "allow", code: "ok", message: "compatível" };
}

/** Rótulo/tom p/ badge do tipo de release (usado na lista/painel). */
export function releaseTypeBadge(release) {
  const c = classifyRelease(release?.version, release?.notes, release?.kind);
  if (c.factory_tools_only) return { label: "FACTORY/FE", tone: "err", title: "FE-dev/factory — full-flash pelo Tools" };
  if (c.commercial) return { label: "COMERCIAL", tone: "warn", title: "SKU comercial (loja)" };
  if (c.plaintext_migration) return { label: "MIGRAÇÃO", tone: "warn", title: "Migração plaintext (pode resetar config)" };
  if (c.provider) return { label: "PROVEDOR", tone: "muted", title: "Provedor (cliente/torre)" };
  return { label: "APP", tone: "muted", title: "Firmware app" };
}
